import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import { ANNOTATION_COLOR } from "../constants";
import { icon } from "./object";
import { ANNOTATION_MARKER_STYLE } from "src/domains/shared/constants/annotationMarkerStyle";

/**
 * 어노테이션 벡터 스타일 캐시 (D4).
 *
 * 스타일 결정 속성이 동일한 피처는 캐시된 동일 `Style` 인스턴스를 재사용한다 —
 * 피처/프레임마다 `new Style/Stroke/Fill/Icon/Text` 를 생성하던 비용을 제거한다.
 *
 * 텍스트(title)는 가변이라 캐시 키 폭발을 막기 위해 키에서 제외하고, 캐시된 Style 의 `Text`
 * 인스턴스에 매 호출 `setText` 로 주입한다. OL 은 style 함수의 반환값을 피처 단위로 동기 소비
 * (렌더 instruction 으로 즉시 캡처)하므로 공유 인스턴스에 대한 `setText` 는 안전하다 — OL 공식
 * 예제(kml-earthquakes 등)에서 권장하는 패턴이다.
 *
 * marker 의 아이콘 src(`icon.marker`/`icon.selected`)는 페이지마다 덮어써지는 전역 가변값이라
 * 해결된 src 를 키에 포함해 stale 아이콘 재사용을 막는다.
 */

const issueStyleCache = new Map<string, Style>();
const geoJsonStyleCache = new Map<string, Style>();

export interface IssueFeatureStyleParams {
  type: string;
  title: string;
  isSelected: boolean;
  isTextShown: boolean;
  fontSize: number;
  /** 원본 properties.color (대문자 정규화 전 값) */
  color?: string;
  opacity?: number;
}

/**
 * `IssueGeoJsonLayer` 피처 스타일을 캐시에서 가져온다(없으면 생성). 동일 속성 피처는 동일 인스턴스를 공유.
 */
export function getIssueFeatureStyle({
  type,
  title,
  isSelected,
  isTextShown,
  fontSize,
  color,
  opacity = 1,
}: IssueFeatureStyleParams): Style {
  const markerSrc =
    type === "marker"
      ? isSelected
        ? icon.selected || icon.marker
        : icon.marker
      : "";

  const key = `${type}|${color ?? ""}|${isSelected}|${isTextShown}|${fontSize}|${opacity}|${markerSrc}`;

  let style = issueStyleCache.get(key);
  if (!style) {
    const makeLabel = (offsetY?: number) =>
      new Text({
        text: title,
        font: `${fontSize}px Calibri,sans-serif`,
        fill: new Fill({ color: "#000" }),
        stroke: new Stroke({ color: "#fff", width: 3 }),
        ...(offsetY !== undefined && { offsetY }),
      });

    if (type !== "marker" && type !== "text") {
      const normalizedColor = (
        color ? color.toUpperCase() : "BLUE"
      ) as keyof typeof ANNOTATION_COLOR;
      style = new Style({
        stroke: new Stroke({
          color: isSelected
            ? "rgba(0, 0, 0, 1)"
            : ANNOTATION_COLOR[normalizedColor].stroke(opacity),
          width: 2,
        }),
        fill: new Fill({
          color: isSelected
            ? "rgba(0, 0, 0, 0.6)"
            : ANNOTATION_COLOR[normalizedColor].fill(opacity),
        }),
        text: isTextShown ? makeLabel() : undefined,
      });
    } else if (type === "marker") {
      style = new Style({
        image: new Icon({
          src: markerSrc,
          scale: ANNOTATION_MARKER_STYLE.scale,
          anchor: [0.5, 1],
        }),
        text: isTextShown
          ? makeLabel(ANNOTATION_MARKER_STYLE.labelOffsetYPx)
          : undefined,
      });
    } else {
      style = new Style({
        stroke: new Stroke({
          color: color,
        }),
        text: isTextShown ? makeLabel() : undefined,
      });
    }
    issueStyleCache.set(key, style);
  }

  // title 은 키에서 제외했으므로 매 호출 주입한다(가변 텍스트).
  if (isTextShown) {
    style.getText()?.setText(title);
  }

  return style;
}

export interface GeoJsonFeatureStyleParams {
  /** GeoJSON properties["Text"] — 있으면 텍스트 라벨 스타일 */
  title?: string;
  /** GeoJSON properties["color"] — 텍스트가 없을 때 stroke 색 */
  color?: string;
}

/**
 * `GeoJsonLayer` 피처 스타일을 캐시에서 가져온다(없으면 생성).
 * title 이 있으면 텍스트 라벨, 없으면 단순 stroke.
 */
export function getGeoJsonFeatureStyle({
  title,
  color,
}: GeoJsonFeatureStyleParams): Style {
  if (title) {
    const key = "text";
    let style = geoJsonStyleCache.get(key);
    if (!style) {
      style = new Style({
        text: new Text({
          text: title,
          font: "12px Calibri,sans-serif",
          fill: new Fill({ color: "#000" }),
          stroke: new Stroke({ color: "#fff", width: 3 }),
        }),
      });
      geoJsonStyleCache.set(key, style);
    }
    style.getText()?.setText(title);
    return style;
  }

  const key = `stroke|${color ?? ""}`;
  let style = geoJsonStyleCache.get(key);
  if (!style) {
    style = new Style({
      stroke: new Stroke({ color }),
    });
    geoJsonStyleCache.set(key, style);
  }
  return style;
}
